import { useEffect, useState } from 'react';

/**
 * Custom hook to track the header element's height.
 * Updates on window resize and DOM mutations within the header.
 *
 * @returns The current height of the header element in pixels
 */
export const useHeaderHeight = (): number => {
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('header');
      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };

    // Throttle function to limit calls to once per interval
    let isThrottled = false;
    const throttledUpdate = () => {
      if (isThrottled) return;

      isThrottled = true;
      updateHeaderHeight();

      setTimeout(() => {
        isThrottled = false;
      }, 100);
    };

    // Initial measurement
    updateHeaderHeight();

    // Listen for window resize with throttling
    window.addEventListener('resize', throttledUpdate);

    // Watch for DOM changes in the header (navbar expand/collapse)
    const header = document.querySelector('header');
    let observer: MutationObserver | null = null;

    if (header) {
      observer = new MutationObserver(throttledUpdate);

      observer.observe(header, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    return () => {
      window.removeEventListener('resize', throttledUpdate);
      observer?.disconnect();
    };
  }, []);

  return headerHeight;
};
